// pages/OrderSuccessPage.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  IndianRupee,
  Mail,
  Package,
  ShoppingBag,
  Truck,
} from "lucide-react";
import { fetchOrderById } from "../store";
import "./OrderSuccess.css";

const sectionIconProps = { size: 18, strokeWidth: 1.75 };
const itemImageIconProps = { size: 28, strokeWidth: 1.75 };

const OrderSuccessPage = () => {
  useEffect(() => {
    // Add interactive animations
    const successTitle = document.querySelector(".oss-success-title");
    const successMessages = document.querySelectorAll(".oss-success-message");
    const orderNumber = document.querySelector(".oss-order-number");

    setTimeout(() => {
      if (successTitle) {
        successTitle.style.animation = "oss-slideInUp 0.6s ease-out";
      }
    }, 400);

    setTimeout(() => {
      successMessages.forEach((msg, index) => {
        msg.style.animation = `oss-slideInUp 0.6s ease-out ${index * 0.1}s`;
      });
    }, 600);

    setTimeout(() => {
      if (orderNumber) {
        orderNumber.style.animation = "oss-slideInUp 0.6s ease-out";
      }
    }, 800);

    // Add hover effects to cards
    const cards = document.querySelectorAll(
      ".oss-info-card, .oss-order-details, .oss-order-summary, .oss-timeline"
    );
    const handleMouseEnter = function () {
      this.style.transform = "translateY(-2px)";
      this.style.transition = "transform 0.2s ease";
    };

    const handleMouseLeave = function () {
      this.style.transform = "translateY(0)";
    };

    cards.forEach((card) => {
      card.addEventListener("mouseenter", handleMouseEnter);
      card.addEventListener("mouseleave", handleMouseLeave);
    });

    // Cleanup event listeners
    return () => {
      cards.forEach((card) => {
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get orderId from navigation state
  const { orderId } = location.state || {};

  const mongoUserId = useSelector((state) => state.user.userId || state.user._id);
  const allOrders = useSelector((state) => state.orders.items);

  // Local state for the order
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (!orderId) {
      navigate("/", { replace: true });
      return;
    }
    // Try to find the order in the store
    const foundOrder = allOrders.find(
      (order) => order._id === orderId || order.orderId === orderId
    );
    if (foundOrder) {
      setOrderData(foundOrder);
    } else if (mongoUserId) {
      dispatch(fetchOrderById({ userId: mongoUserId, orderId })).then((res) => {
        if (res.payload) setOrderData(res.payload);
      });
    }
  }, [orderId, allOrders, dispatch, navigate, mongoUserId]);

  // Optionally, update UI if orders change and the order appears
  useEffect(() => {
    if (orderId && !orderData) {
      const foundOrder = allOrders.find(
        (order) => order._id === orderId || order.orderId === orderId
      );
      if (foundOrder) setOrderData(foundOrder);
    }
  }, [allOrders, orderId, orderData]);

  if (!orderData) {
    return (
      <div className="oss-main-container">
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#6b7280",
          }}
        >
          Loading order details...
        </div>
      </div>
    );
  }

  // Destructure orderData for dynamic rendering
  const {
    totals,
    orderId: orderIdFromData,
    items,
    billingInfo,
    shippingInfo,
    payment,
    createdAt,
  } = orderData;

  const handleContinueShopping = () => {
        navigate('/');
    };

  // Helper for formatted delivery date
  const getDeliveryRange = () => {
    if (!createdAt) return "";
    const start = new Date(new Date(createdAt).getTime() + 5 * 24 * 60 * 60 * 1000);
    const end = new Date(new Date(createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
    return (
      start.toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" }) +
      " - " +
      end.toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })
    );
  };

  // Helper for order date
  const getOrderDate = () => {
    if (!createdAt) return "";
    return new Date(createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  };

  // Helper for payment method
  const getPaymentMethod = () => {
    if (payment?.provider && payment?.payPaymentId) {
      return `${payment.provider}`;
    }
    return payment?.provider || "Razorpay";
  };

  // Helper for subtotal items count
  const getTotalItems = () => items?.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div className="oss-body">
      {/* Main Content */}
      <div className="oss-main-container">
        {/* Success Banner */}
        <div className="oss-success-banner">
          <h1 className="oss-success-title">Order Placed Successfully!</h1>
          <p className="oss-success-message">
            Thank you for choosing Sri Shiv Enterprises. Your eco-friendly order is confirmed.
          </p>
          <p className="oss-success-message">
            A confirmation email has been sent to <strong>{billingInfo?.email || "your email"}</strong>
          </p>
          <div className="oss-order-number">Order #{orderIdFromData}</div>
        </div>

        {/* Order timeline (optional): use Lucide step icons if re-enabled */}

        {/* Content Grid */}
        <div className="oss-content-grid">
          {/* Order Details */}
          <div className="oss-order-details">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <ShoppingBag {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Order Items
              </h3>
            </div>
            <div className="oss-section-content">
              {items.map((item, idx) => (
                <div className="oss-order-item" key={item.productId || idx}>
                  <div className="oss-item-image" aria-hidden>
                    <Package {...itemImageIconProps} />
                  </div>
                  <div className="oss-item-details">
                    <h4 className="oss-item-name">{item.name}</h4>
                    <p className="oss-item-specs">
                      {item.bagSize?.cm} • {item.specifications?.gsm} • BF: {item.specifications?.bf}
                    </p>
                    <p className="oss-item-quantity">Quantity: {item.quantity}</p>
                  </div>
                  <div className="oss-item-price">
                    ₹{(item.price?.amount * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="oss-order-summary">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <IndianRupee {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Order Summary
              </h3>
            </div>
            <div className="oss-summary-content">
              <div className="oss-summary-row">
                <span className="oss-summary-label">Subtotal ({getTotalItems()} items)</span>
                <span className="oss-summary-value">₹{totals?.subtotal?.toFixed(2)}</span>
              </div>
              <div className="oss-summary-row">
                <span className="oss-summary-label">Shipping</span>
                <span className="oss-summary-value">{totals?.shipping === 0 ? "Free" : `₹${totals?.shipping?.toFixed(2)}`}</span>
              </div>
              <div className="oss-summary-row">
                <span className="oss-summary-label">Tax</span>
                <span className="oss-summary-value">₹{totals?.tax?.toFixed(2)}</span>
              </div>
              <div className="oss-summary-row">
                <span className="oss-summary-label">Discount</span>
                <span className="oss-summary-value">{totals?.discount ? `-₹${totals.discount.toFixed(2)}` : "—"}</span>
              </div>
              <div className="oss-summary-row oss-summary-total">
                <span className="oss-total-label">Total Paid</span>
                <span className="oss-total-value">₹{totals?.grandTotal?.toFixed(2)}</span>
              </div>
              <div
                style={{
                  marginTop: "20px",
                  paddingTop: "20px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <div className="oss-summary-row">
                  <span className="oss-summary-label">Payment Method</span>
                  <span className="oss-summary-value">{getPaymentMethod()}</span>
                </div>
                <div className="oss-summary-row">
                  <span className="oss-summary-label">Order Date</span>
                  <span className="oss-summary-value">{getOrderDate()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping & Contact Info */}
        <div className="oss-info-grid">
          <div className="oss-info-card">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <Truck {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Shipping Address
              </h3>
            </div>
            <div className="oss-info-content">
              <div className="oss-info-item">
                <div className="oss-info-label">Delivery Address</div>
                <div className="oss-info-value">
                  {shippingInfo?.fullName}<br />
                  {shippingInfo?.addressLine1}<br />
                  {shippingInfo?.addressLine2 && <>{shippingInfo.addressLine2}<br /></>}
                  {shippingInfo?.city}, {shippingInfo?.state} {shippingInfo?.pincode}<br />
                  {shippingInfo?.country}
                </div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Estimated Delivery</div>
                <div className="oss-info-value">{getDeliveryRange()}</div>
              </div>
            </div>
          </div>

          <div className="oss-info-card">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <Mail {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Contact Information
              </h3>
            </div>
            <div className="oss-info-content">
              <div className="oss-info-item">
                <div className="oss-info-label">Email</div>
                <div className="oss-info-value">{billingInfo?.email || "your email"}</div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Phone</div>
                <div className="oss-info-value">{billingInfo?.phone}</div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Order Updates</div>
                <div className="oss-info-value">
                  SMS & Email notifications enabled
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="oss-action-buttons">
          {/* <button className="oss-action-btn oss-primary-btn" onClick={handleViewOrders}>
            Track Your Order
          </button> */}
          <button className="oss-action-btn oss-secondary-btn" onClick={handleContinueShopping}>
            Continue Shopping
          </button>
          {/* <button className="oss-action-btn oss-outline-btn" onClick={handleDownloadInvoice}>
            Download Invoice
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
